import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { ProjectPicker } from '../../components/ui/ProjectPicker';
import {
  permitService,
  ConstructionPermit,
  CreatePermitData,
} from '../../services/permit.service';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';
import { EmptyState } from '../../components/ui/EmptyState';
import { StatusBadge } from '../../components/ui/StatusBadge';

const PERMIT_STATUSES: Array<{
  value: ConstructionPermit['status'];
  label: string;
}> = [
  { value: 'pending', label: 'Pendiente' },
  { value: 'in_review', label: 'En revisión' },
  { value: 'issued', label: 'Emitido' },
  { value: 'expired', label: 'Expirado' },
  { value: 'rejected', label: 'Rechazado' },
];

const fd = (ts: number) =>
  new Date(ts).toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

export function PermitsPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  const [projectId, setProjectId] = useState('');
  const [permits, setPermits] = useState<ConstructionPermit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    permitNumber: '',
    issuedBy: '',
    issuedDate: new Date().toISOString().slice(0, 10),
    expiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10),
    status: 'pending' as ConstructionPermit['status'],
    notes: '',
  });

  const canCreate = ['sys_admin', 'general_manager', 'project_lead', 'data_entry'].includes(
    user?.role ?? ''
  );

  const load = () => {
    if (!projectId) {
      setPermits([]);
      return;
    }
    setLoading(true);
    setError(null);
    permitService
      .getPermits(projectId)
      .then(setPermits)
      .catch((e) => setError(e.message || 'Error al cargar'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [projectId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId || !form.permitNumber.trim() || !form.issuedBy.trim()) {
      showError('Completa proyecto, folio y autoridad');
      return;
    }
    setSaving(true);
    try {
      const data: CreatePermitData = {
        projectId,
        permitNumber: form.permitNumber.trim(),
        issuedBy: form.issuedBy.trim(),
        issuedDate: new Date(form.issuedDate).getTime(),
        expiryDate: new Date(form.expiryDate).getTime(),
        status: form.status,
        notes: form.notes || undefined,
      };
      await permitService.createPermit(data);
      showSuccess('Trámite registrado');
      setShowForm(false);
      setForm({
        permitNumber: '',
        issuedBy: '',
        issuedDate: new Date().toISOString().slice(0, 10),
        expiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
          .toISOString()
          .slice(0, 10),
        status: 'pending',
        notes: '',
      });
      load();
    } catch (e: any) {
      showError(e.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (
    permit: ConstructionPermit,
    status: ConstructionPermit['status']
  ) => {
    try {
      await permitService.updatePermitStatus(permit.id, status);
      showSuccess('Estatus actualizado');
      load();
    } catch (e: any) {
      showError(e.message || 'Error');
    }
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Permisos y Trámites</h1>
          <p className="text-xs text-slate-500">{permits.length} registrados</p>
        </div>
        {canCreate && projectId && (
          <button
            onClick={() => setShowForm(true)}
            className="px-3 py-2 rounded-lg bg-primary text-white text-sm font-semibold"
          >
            + Nuevo
          </button>
        )}
      </div>

      <ProjectPicker value={projectId} onChange={(id) => setProjectId(id)} />

      {!projectId ? (
        <EmptyState
          icon="📋"
          title="Selecciona un proyecto"
          description="Elige un proyecto para ver sus permisos."
        />
      ) : loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : permits.length === 0 ? (
        <EmptyState
          icon="📋"
          title="Sin trámites"
          description="Registra el primer permiso o trámite de construcción."
        />
      ) : (
        <ul className="space-y-2">
          {permits.map((p) => (
            <li key={p.id} className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-start gap-2 mb-1">
                <div className="min-w-0">
                  <p className="text-[11px] font-mono text-slate-500">
                    {p.permitNumber}
                  </p>
                  <p className="text-sm font-semibold text-slate-900 truncate">
                    {p.issuedBy}
                  </p>
                </div>
                <StatusBadge
                  status={p.status}
                  label={PERMIT_STATUSES.find((s) => s.value === p.status)?.label}
                />
              </div>
              <div className="text-[11px] text-slate-500 space-y-0.5 mb-1.5">
                <p>Emitido: {fd(p.issuedDate)}</p>
                <p>Expira: {fd(p.expiryDate)}</p>
              </div>
              {p.notes && (
                <p className="text-xs text-slate-600 mb-1.5 line-clamp-2">
                  {p.notes}
                </p>
              )}
              <div className="flex gap-1.5 pt-1.5 border-t border-slate-100">
                <select
                  value={p.status}
                  onChange={(e) =>
                    handleStatusChange(p, e.target.value as ConstructionPermit['status'])
                  }
                  className="flex-1 px-2 py-1.5 rounded-md border border-slate-200 text-xs"
                >
                  {PERMIT_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Nuevo trámite</h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-slate-400 text-2xl leading-none"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Folio / Número *
                </label>
                <input
                  type="text"
                  required
                  value={form.permitNumber}
                  onChange={(e) => setForm({ ...form, permitNumber: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Autoridad que emite *
                </label>
                <input
                  type="text"
                  required
                  value={form.issuedBy}
                  onChange={(e) => setForm({ ...form, issuedBy: e.target.value })}
                  placeholder="Ej. Municipio de Monterrey"
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Fecha emisión
                  </label>
                  <input
                    type="date"
                    required
                    value={form.issuedDate}
                    onChange={(e) => setForm({ ...form, issuedDate: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Fecha expiración
                  </label>
                  <input
                    type="date"
                    required
                    value={form.expiryDate}
                    onChange={(e) => setForm({ ...form, expiryDate: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Estatus inicial
                </label>
                <select
                  value={form.status}
                  onChange={(e) =>
                    setForm({ ...form, status: e.target.value as ConstructionPermit['status'] })
                  }
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                >
                  {PERMIT_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Notas
                </label>
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm resize-none focus:border-primary focus:outline-none"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 py-2.5 rounded-lg bg-slate-100 text-slate-700 font-semibold text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
                >
                  {saving ? 'Guardando…' : 'Registrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}